import { useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useForm } from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';

import { FiFileText, FiMail, FiSend, FiUsers } from 'react-icons/fi';

import { SelectInput, TextInput } from '../../components/inputs';

import { sendRebrandAnnouncement } from '../../store/user/userActions';

import { errorToast, isValidEmail, successToast } from '../../utils';



const AUDIENCE_OPTIONS = [

	{ value: 'tutors', label: 'Tutors only' },

	{ value: 'students', label: 'Students only' },

	{ value: 'all', label: 'All users' },

];



const SOURCE_OPTIONS = [

	{ value: 'audience', label: 'Database audience' },

	{ value: 'csv', label: 'CSV file' },

];



const audienceLabel = (value) =>

	AUDIENCE_OPTIONS.find((o) => o.value === value)?.label ?? value;



function RebrandAnnouncement() {

	const dispatch = useDispatch();

	const csvInputRef = useRef(null);

	const [loading, setLoading] = useState(false);

	const [confirmOpen, setConfirmOpen] = useState(false);

	const [pendingAction, setPendingAction] = useState(null);

	const [result, setResult] = useState(null);

	const [csvFile, setCsvFile] = useState(null);



	const {

		register,

		handleSubmit,

		control,

		watch,

		formState: { errors },

	} = useForm({

		defaultValues: {

			source: 'audience',

			audience: 'tutors',

			testEmail: '',

		},

	});



	const source = watch('source');

	const audience = watch('audience');

	const testEmail = watch('testEmail')?.trim();

	const useCsv = source === 'csv';



	const onCsvChange = (event) => {

		const file = event.target.files?.[0];

		setCsvFile(file ?? null);

		setResult(null);

	};



	const clearCsv = () => {

		setCsvFile(null);

		if (csvInputRef.current) {

			csvInputRef.current.value = '';

		}

	};



	const runSend = async ({ dryRun }) => {

		if (useCsv && !csvFile) {

			errorToast('Upload a CSV file with email, firstname, and lastname columns');

			return;

		}



		setLoading(true);

		setResult(null);

		try {

			const data = await dispatch(

				sendRebrandAnnouncement({

					...(useCsv ? {} : { audience }),

					dryRun,

					...(testEmail ? { testEmail } : {}),

					...(useCsv && csvFile ? { csvFile } : {}),

				}),

			).unwrap();



			setResult(data);



			if (dryRun) {

				successToast(

					testEmail

						? 'Dry run complete (test email mode).'

						: `Dry run complete — ${data.total} recipient(s).`,

				);

			} else {

				successToast(

					`Sent ${data.sent} of ${data.total} email(s)${

						data.failed ? ` (${data.failed} failed)` : ''

					}.`,

				);

			}

		} catch (err) {

			errorToast(err?.error ?? 'Failed to send announcement');

		} finally {

			setLoading(false);

			setConfirmOpen(false);

			setPendingAction(null);

		}

	};



	const onDryRun = () => {

		setPendingAction('dryRun');

		runSend({ dryRun: true });

	};



	const onSendRequest = () => {

		if (useCsv && !csvFile) {

			errorToast('Upload a CSV file before sending');

			return;

		}

		setPendingAction('send');

		setConfirmOpen(true);

	};



	const onConfirmSend = () => runSend({ dryRun: false });



	return (

		<div className="max-w-2xl mx-auto">

			<div className="mb-8">

				<div className="flex items-center gap-3 mb-2">

					<div className="flex h-11 w-11 items-center justify-center rounded-xl bg-nt-blue/10 text-nt-blue">

						<FiMail size={22} />

					</div>

					<h1 className="text-2xl font-semibold text-nt-blue">

						Rebrand announcement

					</h1>

				</div>

				<p className="text-nt-grey text-sm leading-relaxed max-w-xl">

					Send the &ldquo;NativTalk becomes Ioparla&rdquo; email. Use a dry run or

					test email first, then send to a database audience or a CSV list.

				</p>

			</div>



			<form

				className="bg-white rounded-2xl border border-nt-grey-light/80 p-6 shadow-sm space-y-5"

				onSubmit={handleSubmit(onDryRun)}

			>

				<SelectInput

					name="source"

					label="Recipient source"

					options={SOURCE_OPTIONS}

					control={control}

					errors={errors}

					register={register}

					validationSchema={{ required: 'Source is required' }}

					onChangeSelect={() => {
						setResult(null);
						clearCsv();
					}}

				/>



				{useCsv ? (

					<div className="space-y-2">

						<label className="block text-sm font-medium text-nt-blue">

							CSV file

						</label>

						<input

							ref={csvInputRef}

							type="file"

							accept=".csv,text/csv"

							onChange={onCsvChange}

							className="block w-full text-sm text-nt-grey file:mr-4 file:rounded-lg file:border-0 file:bg-nt-blue/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-nt-blue hover:file:bg-nt-blue/15"

						/>

						{csvFile && (

							<div className="flex items-center justify-between gap-2 text-xs text-nt-grey">

								<span className="inline-flex items-center gap-1.5">

									<FiFileText size={14} />

									{csvFile.name}

								</span>

								<button

									type="button"

									onClick={clearCsv}

									className="font-semibold text-nt-blue hover:underline"

								>

									Remove

								</button>

							</div>

						)}

						<p className="text-xs text-nt-grey leading-relaxed">

							Required columns: <code className="font-mono">email</code>,{' '}

							<code className="font-mono">firstname</code>,{' '}

							<code className="font-mono">lastname</code>. The greeting uses{' '}

							<code className="font-mono">firstname</code> (or &ldquo;there&rdquo; if

							empty). Duplicate emails are skipped.

						</p>

					</div>

				) : (

					<SelectInput

						name="audience"

						label="Audience"

						options={AUDIENCE_OPTIONS}

						control={control}

						errors={errors}

						register={register}

						validationSchema={{ required: 'Audience is required' }}

					/>

				)}



				<TextInput

					name="testEmail"

					label="Test email (optional)"

					type="email"

					placeholder="you@example.com"

					errors={errors}

					register={register}

					control={control}

					validationSchema={{

						validate: (value) =>

							!value?.trim() || isValidEmail(value.trim()) || 'Enter a valid email',

					}}

				/>



				<p className="text-xs text-nt-grey leading-relaxed">

					{testEmail ? (

						useCsv ? (

							<>

								Only the test address receives the email. If that email is in the

								CSV, its <code className="font-mono">firstname</code> is used as the

								greeting; otherwise &ldquo;there&rdquo;.

							</>

						) : (

							<>

								Only the test address will receive the email; audience still sets the

								greeting (Tutor / Student).

							</>

						)

					) : useCsv ? (

						'Leave test email empty to send to every valid row in the CSV.'

					) : (

						'Leave test email empty to target all matching users for the selected audience.'

					)}

				</p>



				<div className="flex flex-wrap gap-3 pt-2">

					<button

						type="submit"

						disabled={loading}

						className="inline-flex items-center gap-2 rounded-xl border-2 border-nt-blue px-5 py-2.5 text-sm font-semibold text-nt-blue hover:bg-nt-blue/5 disabled:opacity-50 transition-colors"

					>

						<FiUsers size={16} />

						{loading && pendingAction === 'dryRun' ? 'Running…' : 'Dry run'}

					</button>

					<button

						type="button"

						disabled={loading}

						onClick={handleSubmit(onSendRequest)}

						className="inline-flex items-center gap-2 rounded-xl bg-nt-blue px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50 transition-opacity"

					>

						<FiSend size={16} />

						{loading && pendingAction === 'send' ? 'Sending…' : 'Send emails'}

					</button>

				</div>

			</form>



			{result && (

				<div className="mt-6 rounded-2xl border border-nt-grey-light bg-white p-6 shadow-sm">

					<h2 className="text-sm font-semibold text-nt-blue mb-4 uppercase tracking-wide">

						Last result

					</h2>

					<dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">

						<div>

							<dt className="text-nt-grey">Total</dt>

							<dd className="text-lg font-semibold text-nt-blue">{result.total}</dd>

						</div>

						<div>

							<dt className="text-nt-grey">Sent</dt>

							<dd className="text-lg font-semibold text-green-700">{result.sent}</dd>

						</div>

						<div>

							<dt className="text-nt-grey">Failed</dt>

							<dd className="text-lg font-semibold text-nt-red">{result.failed}</dd>

						</div>

						<div>

							<dt className="text-nt-grey">Source</dt>

							<dd className="font-medium text-nt-blue">

								{useCsv ? 'CSV file' : audienceLabel(audience)}

							</dd>

						</div>

					</dl>

					{result.failedEmails?.length > 0 && (

						<div className="mt-4 pt-4 border-t border-nt-grey-light">

							<p className="text-xs font-semibold text-nt-red mb-2">Failed addresses</p>

							<ul className="text-xs text-nt-grey space-y-1 max-h-32 overflow-y-auto">

								{result.failedEmails.map((email) => (

									<li key={email}>{email}</li>

								))}

							</ul>

						</div>

					)}

					{result.recipients?.length > 0 && result.sent === 0 && (

						<div className="mt-4 pt-4 border-t border-nt-grey-light">

							<p className="text-xs font-semibold text-nt-grey mb-2">

								Recipients ({result.recipients.length})

							</p>

							<ul className="text-xs text-nt-grey space-y-1 max-h-40 overflow-y-auto font-mono">

								{result.recipients.slice(0, 50).map((email) => (

									<li key={email}>{email}</li>

								))}

								{result.recipients.length > 50 && (

									<li className="text-nt-grey italic">

										…and {result.recipients.length - 50} more

									</li>

								)}

							</ul>

						</div>

					)}

				</div>

			)}



			<AnimatePresence>

				{confirmOpen && (

					<div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="presentation">

						<motion.div

							initial={{ opacity: 0 }}

							animate={{ opacity: 1 }}

							exit={{ opacity: 0 }}

							className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"

							onClick={() => !loading && setConfirmOpen(false)}

							aria-hidden

						/>

						<motion.div

							role="dialog"

							aria-modal="true"

							aria-labelledby="rebrand-send-title"

							initial={{ opacity: 0, scale: 0.96, y: 8 }}

							animate={{ opacity: 1, scale: 1, y: 0 }}

							exit={{ opacity: 0, scale: 0.96, y: 8 }}

							className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-6"

						>

							<h2 id="rebrand-send-title" className="text-lg font-semibold text-nt-blue mb-2">

								Send announcement?

							</h2>

							<p className="text-sm text-nt-grey leading-relaxed mb-6">

								{testEmail ? (

									<>

										This will send one email to <strong>{testEmail}</strong>.

									</>

								) : useCsv ? (

									<>

										This will send the rebrand email to all valid rows in{' '}

										<strong>{csvFile?.name}</strong>. Run a dry run first if you

										have not already.

									</>

								) : (

									<>

										This will send the rebrand email to all active{' '}

										<strong>{audienceLabel(audience)}</strong>. Run a dry run first

										if you have not already.

									</>

								)}

							</p>

							<div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">

								<button

									type="button"

									disabled={loading}

									onClick={() => setConfirmOpen(false)}

									className="rounded-xl border-2 border-nt-grey-light px-5 py-2.5 text-sm font-semibold text-nt-blue hover:bg-nt-grey-light/30 disabled:opacity-50"

								>

									Cancel

								</button>

								<button

									type="button"

									disabled={loading}

									onClick={onConfirmSend}

									className="rounded-xl bg-nt-blue px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"

								>

									{loading ? 'Sending…' : 'Confirm send'}

								</button>

							</div>

						</motion.div>

					</div>

				)}

			</AnimatePresence>

		</div>

	);

}



export { RebrandAnnouncement };

